import React, { useCallback, useEffect, useState } from 'react';
import { Pause, Play, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

/** B・急停控制（需求 B，2026-10-18）：一鍵制停 AI 自動回覆 30 分鐘，到期自動恢復 */
const AiPauseControl: React.FC = () => {
  const [paused, setPaused] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/public/admin/ai-assistant/pause-status');
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        const data = d?.data ?? d ?? {};
        setPaused(!!data.paused);
        setRemainingSec(Number(data.remainingSec || 0));
      }
    } catch { /* 靜默：控制項唔該打擾主流程 */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // 每秒倒數（只喺暫停時）
  useEffect(() => {
    if (!paused) return;
    const id = setInterval(() => setRemainingSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [paused]);

  const pause = async () => {
    if (!confirm('確定停止 AI 客服 30 分鐘？期間 AI 對所有客人靜音（訊息照收照記，人手跟）。')) return;
    setBusy(true);
    try {
      await apiFetch('/api/public/admin/ai-assistant/pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes: 30 }),
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await apiFetch('/api/public/admin/ai-assistant/resume', { method: 'POST' });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div
      className={`fixed right-4 top-[72px] z-40 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg border transition ${
        paused
          ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/60 dark:border-amber-700 dark:text-amber-200'
          : 'bg-white border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
      }`}
      title="AI 客服急停／恢復（急停期間 AI 對所有客人靜音，訊息照收照記）"
    >
      {paused ? (
        <>
          <span className="flex items-center gap-1"><Pause size={12} /> AI 已停止</span>
          <span className="tabular-nums font-mono">{fmt(remainingSec)}</span>
          <button onClick={resume} disabled={busy}
                  className="flex items-center gap-1 rounded-full bg-green-600 text-white px-2 py-0.5 hover:bg-green-700 disabled:opacity-50">
            {busy ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} />} 恢復
          </button>
        </>
      ) : (
        <button onClick={pause} disabled={busy} className="flex items-center gap-1 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Pause size={12} />} 停止 AI 客服 30 分鐘
        </button>
      )}
    </div>
  );
};

export default AiPauseControl;
